import { useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Presentation, Timer } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Blocks, type Block } from './Blocks';
import { Rich } from './Rich';

export interface Slide {
  /** Short label for the progress rail and the slide index. */
  nav: string;
  kicker: string;
  title: string;
  lead?: string;
  blocks: Block[];
}

interface SlideDeckProps {
  slides: Slide[];
  /** Route of the companion one-pager. */
  onePagerPath: string;
  /** Confidentiality line under every slide. */
  footer: string;
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/** A keyboard- and URL-navigable slide deck: `?slide=N`, arrow keys, Home and End. */
export function SlideDeck({ slides, onePagerPath, footer }: SlideDeckProps) {
  const total = slides.length;
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = Number.parseInt(searchParams.get('slide') ?? '1', 10);
  const current = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), total) : 1;
  const slide = slides[current - 1];

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 1), total);
      if (clamped === current) return;
      setSearchParams({ slide: String(clamped) }, { replace: true });
      document.getElementById('deck-top')?.scrollIntoView({ block: 'start' });
    },
    [current, total, setSearchParams]
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') goTo(current + 1);
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') goTo(current - 1);
      if (event.key === 'Home') goTo(1);
      if (event.key === 'End') goTo(total);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [current, total, goTo]);

  return (
    <main className="flex-1 overflow-y-auto">
      <div id="deck-top" className="mx-auto max-w-5xl px-4 pb-10 pt-20 sm:px-6 md:py-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-2">
            <Presentation className="h-4 w-4 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Pitch Deck</span>
            <span className="text-sm text-slate-500">·</span>
            <span className="text-sm tabular-nums text-slate-400">
              {pad(current)} / {pad(total)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => goTo(current - 1)}
              disabled={current === 1}
              leftIcon={<ChevronLeft className="h-4 w-4" />}
              aria-label="Previous slide"
            >
              Prev
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => goTo(current + 1)}
              disabled={current === total}
              rightIcon={<ChevronRight className="h-4 w-4" />}
              aria-label="Next slide"
            >
              Next
            </Button>
          </div>
        </div>

        <div className="mb-8 flex gap-1" aria-hidden="true">
          {slides.map((s, i) => (
            <button
              key={s.nav}
              type="button"
              onClick={() => goTo(i + 1)}
              title={`${pad(i + 1)} · ${s.nav}`}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                i + 1 === current
                  ? 'bg-indigo-400'
                  : i + 1 < current
                    ? 'bg-indigo-500/40'
                    : 'bg-slate-700/60 hover:bg-slate-600'
              )}
            />
          ))}
        </div>

        <article className="mb-10">
          <div className="mb-8">
            <div className="mb-3 flex flex-wrap items-center gap-2.5">
              <Badge variant="premium">{pad(current)}</Badge>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{slide.kicker}</p>
            </div>
            <h1
              className={cn(
                'font-extrabold tracking-heading text-white',
                current === 1
                  ? 'text-hero-mobile md:text-[3rem] md:leading-[1.08]'
                  : 'text-section-mobile md:text-[2.25rem] md:leading-[1.15]'
              )}
            >
              {slide.title}
            </h1>
            {slide.lead && (
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-400 sm:text-base">
                <Rich text={slide.lead} />
              </p>
            )}
          </div>
          <div className="space-y-4 sm:space-y-5">
            <Blocks blocks={slide.blocks} />
          </div>
        </article>

        <nav className="mb-8 flex flex-wrap gap-2">
          {slides.map((s, i) => (
            <button
              key={s.nav}
              type="button"
              onClick={() => goTo(i + 1)}
              className={cn(
                'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
                i + 1 === current
                  ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
                  : 'border-slate-700/50 text-slate-500 hover:border-slate-600 hover:text-slate-300'
              )}
            >
              <span className="tabular-nums">{pad(i + 1)}</span>
              <span className="ml-1.5">{s.nav}</span>
            </button>
          ))}
        </nav>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-slate-700/50 pt-5 sm:flex-row sm:items-center">
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Timer className="h-3.5 w-3.5 shrink-0" />
            {footer}
          </p>
          <Link
            to={onePagerPath}
            className="inline-flex shrink-0 items-center gap-1.5 text-xs text-indigo-300 transition-colors hover:text-indigo-200"
          >
            <FileText className="h-3.5 w-3.5" />
            One-pager
          </Link>
        </div>
      </div>
    </main>
  );
}
